import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { get } from '../../api/client';
import type { LeadsPage, PmOverview } from '../../api/types';
import { useMe } from '../../auth';
import { IconExternal } from '../../components/icons';
import { PageHead, Spinner, timeAgo } from '../../components/ui';
import { useT } from '../../i18n';
import { LeadItem } from '../Leads';

/** Portada del dashboard de Phelon & Moore. */
export function PmHome() {
  const { t } = useT();
  const { data: me } = useMe();
  const { data, isPending } = useQuery({ queryKey: ['overview'], queryFn: () => get<PmOverview>('/admin/overview') });
  const latest = useQuery({ queryKey: ['leads', 'list', 'home'], queryFn: () => get<LeadsPage>('/admin/leads?status=inbox&pageSize=5') });

  return (
    <>
      <PageHead kicker={t('Panel')} title={t('Hola, {name}', { name: me?.name.split(' ')[0] ?? '' })} description={t('Lo que pasa hoy en la landing de Phelon & Moore.')}>
        {data?.siteUrl && (
          <a className="btn" href={data.siteUrl} target="_blank" rel="noreferrer">
            <IconExternal /> {t('Ver el sitio')}
          </a>
        )}
      </PageHead>

      {isPending || !data ? (
        <Spinner />
      ) : (
        <div className="stack">
          <div className="grid grid-4 stats">
            <Link to="/mensajes" className="panel stat">
              <span className="label">{t('Mensajes nuevos')}</span>
              <span className="value" style={{ color: data.leads.new ? 'var(--accent-text)' : undefined }}>
                {data.leads.new}
              </span>
              <span className="hint">{data.leads.lastAt ? t('Último: {when}', { when: timeAgo(data.leads.lastAt) }) : t('Todavía no llega ninguno')}</span>
            </Link>
            <Link to="/motos" className="panel stat">
              <span className="label">{t('Motos visibles')}</span>
              <span className="value">
                {data.lineup.visible}
                <span className="faint" style={{ fontSize: 18 }}> / {data.lineup.total}</span>
              </span>
              <span className="hint">{data.lineup.total ? t('en la gama y la línea de producto') : t('la landing usa la gama de su código')}</span>
            </Link>
            <Link to="/noticias" className="panel stat">
              <span className="label">{t('Noticias visibles')}</span>
              <span className="value">
                {data.news.visible}
                <span className="faint" style={{ fontSize: 18 }}> / {data.news.total}</span>
              </span>
              <span className="hint">{t('en el carrusel y en /news/')}</span>
            </Link>
            <Link to="/historia" className="panel stat">
              <span className="label">{t('Historia')}</span>
              <span className="value">
                {data.history.visible}
                <span className="faint" style={{ fontSize: 18 }}> / {data.history.total}</span>
              </span>
              <span className="hint">{t('hitos en la línea de tiempo')}</span>
            </Link>
            <Link to="/publicar" className="panel stat">
              <span className="label">{t('Landing')}</span>
              <span className="value" style={{ fontSize: 20, marginTop: 6 }}>
                {data.publish.publishing ? (
                  <span className="badge info">{t('Publicando…')}</span>
                ) : data.publish.upToDate ? (
                  <span className="badge ok">{t('Al día')}</span>
                ) : (
                  <span className="badge warn">{t('Cambios sin publicar')}</span>
                )}
              </span>
              <span className="hint">{data.publish.publishedAt ? t('Publicada {when}', { when: timeAgo(data.publish.publishedAt) }) : t('Todavía no se publica')}</span>
            </Link>
            <div className="panel stat">
              <span className="label">{t('Avisos por correo')}</span>
              <span className="value" style={{ fontSize: 20, marginTop: 6 }}>
                {data.notifications ? <span className="badge ok">{t('Activos')}</span> : <span className="badge warn">{t('Sin configurar')}</span>}
              </span>
              <span className="hint">{data.notifications ? t('Cada mensaje nuevo llega al correo') : t('Los mensajes solo quedan aquí')}</span>
            </div>
            <Link to="/mensajes" className="panel stat">
              <span className="label">CRM (GoHighLevel)</span>
              <span className="value" style={{ fontSize: 20, marginTop: 6 }}>
                {data.crm.configured === null ? (
                  <span className="badge warn">{t('Sin conexión')}</span>
                ) : !data.crm.configured ? (
                  <span className="badge warn">{t('Sin configurar')}</span>
                ) : data.crm.failed ? (
                  <span className="badge red">{t('{n} sin enviar', { n: data.crm.failed })}</span>
                ) : data.crm.test ? (
                  <span className="badge info">{t('Activo · pruebas')}</span>
                ) : (
                  <span className="badge ok">{t('Activo')}</span>
                )}
              </span>
              <span className="hint">
                {data.crm.configured === null
                  ? t('No se pudo leer la URL del CRM')
                  : !data.crm.configured
                    ? t('Falta la URL del webhook: las solicitudes solo quedan aquí')
                    : data.crm.test
                      ? t('Llegan al CRM con region «Test» (landing en pruebas)')
                      : t('Cada solicitud de distribuidor llega al CRM')}
                {data.crm.pending ? ` · ${t('{n} esperando reintento', { n: data.crm.pending })}` : ''}
              </span>
            </Link>
          </div>

          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="row between panel-pad" style={{ paddingBottom: 12 }}>
              <h2 style={{ margin: 0 }}>{t('Últimos mensajes')}</h2>
              <Link to="/mensajes" className="btn btn-sm btn-ghost">
                {t('Ver la bandeja')}
              </Link>
            </div>
            {latest.isPending ? (
              <div className="panel-pad" style={{ paddingTop: 0 }}>
                <Spinner />
              </div>
            ) : latest.data?.items.length ? (
              <div style={{ borderTop: '1px solid var(--line)' }}>
                {latest.data.items.map((l) => (
                  <LeadItem key={l.id} lead={l} to={`/mensajes/${l.id}`} />
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ paddingTop: 12 }}>
                {t('Todavía no llega ningún mensaje por el formulario de socios.')}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
